import { Box, type BoxProps, useComputedColorScheme } from "@mantine/core";

import type { SharedWidgetAppearance } from "./customization-types";

type WidgetLogoConfig = NonNullable<SharedWidgetAppearance["logoUrl"]>;

export interface WidgetLogoProps extends Omit<BoxProps, "component"> {
  logoUrl?: WidgetLogoConfig;
  width?: number | string;
  height?: number | string;
  margin?: number | string;
  asHeader?: boolean;
  alt?: string;
}

function resolveLogoSrc(logoUrl: WidgetLogoConfig | undefined, colorScheme: "light" | "dark") {
  if (!logoUrl) {
    return null;
  }

  if (typeof logoUrl === "string") {
    return logoUrl.trim() || null;
  }

  return colorScheme === "dark" ? logoUrl.lightLogo : logoUrl.darkLogo;
}

export function WidgetLogo({
  logoUrl,
  width,
  height,
  margin = "0 auto",
  asHeader = true,
  alt = "Widget Logo",
  style,
  ...boxProps
}: WidgetLogoProps) {
  const colorScheme = useComputedColorScheme("light");
  const logoSrc = resolveLogoSrc(logoUrl, colorScheme);

  if (!logoSrc) {
    return null;
  }

  const defaultWidth = asHeader && !width && !height ? 200 : undefined;
  const imageStyle = {
    ...(width && { width }),
    ...(defaultWidth && { width: defaultWidth }),
    ...(height && { height }),
    ...(margin && { margin }),
    ...style,
  };

  if (asHeader) {
    return (
      <Box component="header" m="0 auto" bg="transparent !important" {...boxProps}>
        <Box component="img" src={logoSrc} alt={alt} style={imageStyle} />
      </Box>
    );
  }

  return <Box component="img" src={logoSrc} alt={alt} style={imageStyle} {...boxProps} />;
}
